import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../../../i18n';
import { dateLocale } from '../../../lib/localePrefs';
import { Meteor } from 'meteor/meteor';
import { useMethod } from '../../../lib/hooks';
import {
  useOpsAccessibleRestaurants,
  useOpsScopeAttributes,
  useOpsScopePeriodMetrics,
} from '../hooks/useOpsCommand';
import type { OpsScopeConfigPayload } from '../types';
import { restaurantMatchesSearch } from '../lib/restaurantSearch';
import {
  attributeFamiliesForRules,
  buildScopeRestaurantProfiles,
  createEmptyFilterState,
  evaluateScopeFilters,
  scopeFilterField,
  settingsValueLabel,
  summarizeFilterState,
  type ScopeEvalContext,
  type ScopeFilterState,
  type ScopeRestaurantProfile,
} from '../lib/scopeGroupFilters';
import {
  formatKpiValue,
  indexScopePeriodMetrics,
  isScopeKpiRule,
  kpiRuleMetricName,
  planScopeKpiRequests,
  scopeKpiValue,
  type ResolvedPeriod,
  type ScopeKpiIndex,
} from '../lib/scopeKpi';
import {
  buildScopeGroupRecipe,
  diffMembership,
  membershipEditedSince,
  recipeOf,
  rerunMembership,
  type ScopeGroupRecipe,
} from '../lib/scopeGroupRecipe';
import { ScopeGroupFilters, type ScopeRuleStatus } from './ScopeGroupFilters';
import { Icon } from './shared/Icon';

type ScopeGroupsModalProps = {
  restaurantId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

const RESTAURANT_RENDER_CAP = 300;
const RERUN_NAME_PREVIEW = 12;

/** Per-row values for the active rules, so users can see why a row matched. */
function ruleValueChips(
  r: ScopeRestaurantProfile,
  state: ScopeFilterState,
  kpiByRule: Map<string, ScopeKpiIndex>,
): string[] {
  const chips: string[] = [];
  for (const rule of state.rules) {
    if (isScopeKpiRule(rule)) {
      const index = kpiByRule.get(rule.id);
      if (!index) continue;
      const { value, base } = scopeKpiValue(index, r.id, rule);
      let chip = `${kpiRuleMetricName(rule)}: ${formatKpiValue(value, rule)}`;
      if (base != null && value != null) {
        chip += ` (${i18n.t('settings:groups.nReservations', { count: base })})`;
      }
      chips.push(chip);
      continue;
    }
    const def = scopeFilterField(rule.field);
    if (def?.category === 'settings' && r.settings) {
      const value = r.settings[rule.field as keyof NonNullable<typeof r.settings>];
      chips.push(`${def.label}: ${settingsValueLabel(rule.field, value)}`);
    }
  }
  return chips;
}

function formatRecipeDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(dateLocale(currentLocale()), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

type RerunState = {
  recipe: ScopeGroupRecipe;
  /** Membership when Re-run was clicked — the diff baseline. */
  baseline: string[];
  keepManual: boolean;
};

export function ScopeGroupsModal({
  restaurantId,
  onClose,
  onSaved,
}: ScopeGroupsModalProps) {
  const { t } = useTranslation('settings');
  const config = useMethod<OpsScopeConfigPayload>(
    'opsCommand/getScopeConfig',
    [restaurantId],
  );
  // Custom groups are cross-fleet: offer every restaurant the user can open.
  // Location filters need nothing more; settings and period KPIs are fetched
  // only while a rule needs them.
  const accessible = useOpsAccessibleRestaurants();

  const [search, setSearch] = useState('');
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Checked-first ordering uses a snapshot so ticking a row never moves it.
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [filterState, setFilterState] = useState<ScopeFilterState>(
    createEmptyFilterState(),
  );
  const [assignedOnly, setAssignedOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeRecipe, setActiveRecipe] = useState<ScopeGroupRecipe | null>(null);
  const [rerun, setRerun] = useState<RerunState | null>(null);

  // The person's own groups for this customer, kept on the server (decision 7.7.2).
  const groups = useMemo(() => config.data?.groups ?? [], [config.data?.groups]);
  const restaurants =
    accessible.data?.restaurants ?? config.data?.restaurants ?? [];

  // ---- data for the active rules -------------------------------------------
  const attributeFamilies = useMemo(
    () => attributeFamiliesForRules(filterState.rules),
    [filterState.rules],
  );
  const attributes = useOpsScopeAttributes(attributeFamilies);

  const kpiPlan = useMemo(
    () => planScopeKpiRequests(filterState.rules.filter(isScopeKpiRule)),
    [filterState.rules],
  );
  // Fixed hook slots (rules of hooks); MAX_SCOPE_KPI_PERIODS = 3.
  const metricSlots = [
    useOpsScopePeriodMetrics(kpiPlan.requests[0] ?? null),
    useOpsScopePeriodMetrics(kpiPlan.requests[1] ?? null),
    useOpsScopePeriodMetrics(kpiPlan.requests[2] ?? null),
  ];
  const slotIndexes = useMemo(
    () =>
      metricSlots.map((slot, i) => {
        const request = kpiPlan.requests[i];
        const data = slot.data;
        // Never evaluate a payload from another period.
        if (!request || !data || data.start !== request.start || data.end !== request.end) {
          return null;
        }
        return indexScopePeriodMetrics(data);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kpiPlan, metricSlots[0].data, metricSlots[1].data, metricSlots[2].data],
  );
  const kpiByRule = useMemo(() => {
    const map = new Map<string, ScopeKpiIndex>();
    for (const [ruleId, slot] of kpiPlan.requestByRule) {
      const index = slotIndexes[slot];
      if (index) map.set(ruleId, index);
    }
    return map;
  }, [kpiPlan, slotIndexes]);
  const currencies = useMemo(
    () => [...new Set(slotIndexes.flatMap(ix => ix?.payload.currencies ?? []))].sort(),
    [slotIndexes],
  );

  const errorMessage = (e: unknown) =>
    (e as { reason?: string; message?: string } | undefined)?.reason ??
    (e as { message?: string } | undefined)?.message ??
    null;

  const attributesLoading =
    attributeFamilies.length > 0 && !attributes.data && !attributes.error;
  const attributesError = attributeFamilies.length > 0 ? errorMessage(attributes.error) : null;

  const ruleStatus = useMemo(() => {
    const map = new Map<string, ScopeRuleStatus>();
    for (const rule of filterState.rules) {
      if (isScopeKpiRule(rule)) {
        const slot = kpiPlan.requestByRule.get(rule.id);
        const swr = slot !== undefined ? metricSlots[slot] : undefined;
        map.set(rule.id, {
          resolved: kpiPlan.resolvedByRule.get(rule.id),
          invalid: kpiPlan.errors.get(rule.id) ?? null,
          loading: slot !== undefined && !slotIndexes[slot] && !swr?.error,
          error: swr?.error ? (errorMessage(swr.error) ?? t('groups.loadFailed')) : null,
          onRetry: swr ? () => void swr.mutate() : undefined,
        });
      } else if (scopeFilterField(rule.field)?.needs) {
        map.set(rule.id, {
          loading: attributesLoading,
          error: attributesError,
          onRetry: () => void attributes.mutate(),
        });
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState.rules,
    kpiPlan,
    slotIndexes,
    attributesLoading,
    attributesError,
    metricSlots[0].error,
    metricSlots[1].error,
    metricSlots[2].error,
  ]);

  const evaluationPending = [...ruleStatus.values()].some(s => s.loading);
  const evaluationBroken = [...ruleStatus.values()].some(s => s.error || s.invalid);
  const evaluationReady = !evaluationPending && !evaluationBroken;

  const profiles = useMemo(
    () => buildScopeRestaurantProfiles(restaurants, attributes.data),
    [restaurants, attributes.data],
  );

  const evalContext: ScopeEvalContext = useMemo(() => ({ kpiByRule }), [kpiByRule]);

  /** Every restaurant passing the filters (ignores search / Selected only). */
  const filterMatches = useMemo(() => {
    if (!filterState.rules.length) return profiles;
    if (!evaluationReady) return [];
    return profiles.filter(r => evaluateScopeFilters(r, filterState, evalContext));
  }, [profiles, filterState, evalContext, evaluationReady]);

  const selectedOnlyActive = assignedOnly && selectedIds.size > 0;
  const filteredRestaurants = useMemo(() => {
    const matched = filterMatches.filter(r => {
      if (selectedOnlyActive && !selectedIds.has(r.id)) {
        return false;
      }
      return restaurantMatchesSearch(r, search);
    });
    return matched.sort((a, b) => {
      const aOn = pinnedIds.has(a.id) ? 0 : 1;
      const bOn = pinnedIds.has(b.id) ? 0 : 1;
      if (aOn !== bOn) return aOn - bOn;
      return a.name.localeCompare(b.name);
    });
  }, [filterMatches, search, selectedOnlyActive, selectedIds, pinnedIds]);

  // Only a bounded slice is rendered so a full 7k-restaurant list stays
  // responsive; bulk selection acts on every match, not just this slice.
  const visibleRestaurants = useMemo(
    () => filteredRestaurants.slice(0, RESTAURANT_RENDER_CAP),
    [filteredRestaurants],
  );

  const resetFilters = () => {
    setSearch('');
    setFilterState(createEmptyFilterState());
    setAssignedOnly(false);
    setRerun(null);
  };

  const loadGroup = (
    group: { id: string; name: string; restaurantIds: string[]; recipe?: unknown } | null,
  ) => {
    const ids = new Set(group?.restaurantIds ?? []);
    setCreatingNew(!group);
    setActiveGroupId(group?.id ?? null);
    setDraftName(group?.name ?? '');
    setSelectedIds(ids);
    setPinnedIds(ids);
    setActiveRecipe(group ? recipeOf(group.recipe) : null);
    resetFilters();
    setError(null);
  };

  useEffect(() => {
    if (initialized || config.isLoading) {
      return;
    }
    loadGroup(groups[0] ?? null);
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, config.isLoading, initialized]);

  const startNewGroup = () => loadGroup(null);

  const selectGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group) loadGroup(group);
  };

  const toggleRestaurant = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllMatches = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const r of filteredRestaurants) next.add(r.id);
      return next;
    });
  };

  const clearAllMatches = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const r of filteredRestaurants) next.delete(r.id);
      return next;
    });
  };

  // ---- re-run a saved recipe -------------------------------------------------
  const startRerun = () => {
    if (!activeRecipe) return;
    setSearch('');
    setAssignedOnly(false);
    // Relative presets re-resolve to today; custom ranges stay fixed.
    setFilterState(activeRecipe.state);
    setRerun({ recipe: activeRecipe, baseline: [...selectedIds], keepManual: true });
  };

  const rerunResult = useMemo(() => {
    if (!rerun || !evaluationReady) return null;
    const next = rerunMembership(
      filterMatches.map(r => r.id),
      rerun.recipe,
      rerun.keepManual,
    );
    return { next, ...diffMembership(rerun.baseline, next) };
  }, [rerun, evaluationReady, filterMatches]);

  const nameById = useMemo(
    () => new Map(restaurants.map(r => [r.id, r.name])),
    [restaurants],
  );

  const applyRerun = () => {
    if (!rerunResult) return;
    setSelectedIds(new Set(rerunResult.next));
    setPinnedIds(new Set(rerunResult.next));
    setRerun(null);
  };

  // ---- persistence -----------------------------------------------------------
  const formatError = (e: unknown) => {
    const err = e as Meteor.Error & {
      details?: { errors?: { name?: string; type?: string; message?: string }[] };
    };
    const base = err.reason ?? err.message ?? t('groups.saveFailed');
    const fieldErrors = err.details?.errors
      ?.map(item => {
        if (item.message) {
          return item.message;
        }
        if (item.name && item.type) {
          return `${item.name} (${item.type})`;
        }
        return '';
      })
      .filter(Boolean)
      .join('; ');
    return fieldErrors ? `${base}: ${fieldErrors}` : base;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const wasCreate = creatingNew;
      const ids = [...selectedIds];
      // Snapshot + refresh: remember how the membership was built. Without
      // filters the saved recipe stays as it was.
      const recipe =
        filterState.rules.length && evaluationReady
          ? buildScopeGroupRecipe({
              state: filterState,
              resolvedByRule: kpiPlan.resolvedByRule,
              matchedIds: filterMatches.map(r => r.id),
              selectedIds: ids,
            })
          : null;
      // The person's own group, kept on the server for this customer; the
      // server checks every venue is one they can open.
      const savedId = await Meteor.callPromise<string>(
        'opsCommand/saveScopeGroup',
        restaurantId,
        {
          id: activeGroupId ?? undefined,
          name: draftName.trim(),
          restaurantIds: ids,
          ...(recipe ? { recipe } : {}),
        },
      );
      setRerun(null);
      const updated = await config.mutate();
      await onSaved();

      const saved = updated?.groups?.find(g => g.id === savedId);
      setActiveRecipe(recipe ?? recipeOf(saved?.recipe));
      setCreatingNew(false);
      setActiveGroupId(saved?.id ?? (wasCreate ? savedId : activeGroupId));
      if (saved) {
        setDraftName(saved.name);
        setSelectedIds(new Set(saved.restaurantIds));
        setPinnedIds(new Set(saved.restaurantIds));
        setAssignedOnly(false);
      }
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeGroupId) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await Meteor.callPromise(
        'opsCommand/deleteScopeGroup',
        restaurantId,
        activeGroupId,
      );
      const remaining = groups.filter(g => g.id !== activeGroupId);
      loadGroup(remaining[0] ?? null);
      await config.mutate();
      await onSaved();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const isBuiltinGroup =
    !!activeGroupId && activeGroupId.startsWith('builtin:');
  const hasActiveFilters =
    filterState.rules.length > 0 || assignedOnly || search.trim().length > 0;
  const listLoading = accessible.isLoading && !accessible.data;
  const matchCount = filteredRestaurants.length;
  const allMatchesSelected =
    matchCount > 0 && filteredRestaurants.every(r => selectedIds.has(r.id));
  const recipeResolved = useMemo(
    () =>
      activeRecipe
        ? new Map<string, ResolvedPeriod>(Object.entries(activeRecipe.resolvedPeriods))
        : undefined,
    [activeRecipe],
  );
  const recipeEdited =
    !!activeRecipe && membershipEditedSince(activeRecipe, selectedIds);

  return (
    <>
      <div className="scrim" onClick={onClose} role="presentation" />
      <div
        className="scope-modal"
        role="dialog"
        aria-labelledby="scope-modal-title"
        data-ftue="scope-groups-modal"
      >
        <div className="scope-modal-head">
          <div>
            <h2 id="scope-modal-title">{t('groups.titleModal')}</h2>
            <p>{t('groups.lead')}</p>
          </div>
          <button
            type="button"
            className="ghost-btn"
            onClick={onClose}
            aria-label={t('groups.close')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="scope-modal-body">
          <div className="scope-modal-side">
            <div className="scope-modal-side-head">
              <span>{t('groups.list')}</span>
            </div>
            <div className="scope-group-list">
              <button
                type="button"
                className={`scope-group-item scope-group-item-new${
                  creatingNew ? ' on' : ''
                }`}
                onClick={startNewGroup}
              >
                <b>{t('groups.newGroup')}</b>
                <span>{creatingNew ? t('groups.draft') : t('groups.createShort')}</span>
              </button>
              {groups.map(g => (
                <button
                  key={g.id}
                  type="button"
                  className={`scope-group-item${
                    !creatingNew && activeGroupId === g.id ? ' on' : ''
                  }`}
                  onClick={() => selectGroup(g.id)}
                >
                  <b>{g.name}</b>
                  <span>{t('groups.nRestaurants', { count: g.restaurantIds.length })}</span>
                </button>
              ))}
              {!groups.length && !config.isLoading && !creatingNew && (
                <div className="scope-group-empty">
                  {t('groups.noSaved')}
                </div>
              )}
            </div>
          </div>

          <div className="scope-modal-main">
            <div className="scope-main-setup">
              <label className="scope-field scope-field-inline">
                <span>{t('groups.groupName')}</span>
                <input
                  type="text"
                  value={draftName}
                  onChange={e => setDraftName(e.target.value)}
                  placeholder={
                    creatingNew
                      ? t('groups.placeholder')
                      : t('groups.groupName')
                  }
                />
              </label>

              {activeRecipe && !creatingNew && !rerun && (
                <div className="scope-recipe-banner">
                  <div className="scope-recipe-text">
                    <b>{t('groups.builtFrom')}</b>{' '}
                    <span>{summarizeFilterState(activeRecipe.state, recipeResolved)}</span>
                    <span className="scope-recipe-meta">
                      {t('groups.asOf', { date: formatRecipeDate(activeRecipe.evaluatedAt) })}
                      {recipeEdited && <> · {t('groups.membershipEdited')}</>}
                    </span>
                  </div>
                  <button type="button" className="scope-cmp-toggle" onClick={startRerun}>
                    <Icon name="refresh" size={12} />
                    {t('groups.rerun')}
                  </button>
                </div>
              )}

              {rerun && (
                <div className="scope-recipe-banner is-rerun">
                  <div className="scope-recipe-text">
                    {!rerunResult ? (
                      <span>{t('groups.rerunCalculating')}</span>
                    ) : (
                      <>
                        <b>
                          {t('groups.rerunDiff', {
                            added: rerunResult.added.length,
                            removed: rerunResult.removed.length,
                          })}
                        </b>
                        {(rerunResult.added.length > 0 || rerunResult.removed.length > 0) && (
                          <details className="scope-rerun-names">
                            <summary>{t('groups.rerunShowNames')}</summary>
                            {rerunResult.added.length > 0 && (
                              <p>
                                <b>+</b>{' '}
                                {rerunResult.added
                                  .slice(0, RERUN_NAME_PREVIEW)
                                  .map(id => nameById.get(id) ?? id)
                                  .join(', ')}
                                {rerunResult.added.length > RERUN_NAME_PREVIEW &&
                                  ` ${t('groups.andMore', { count: rerunResult.added.length - RERUN_NAME_PREVIEW })}`}
                              </p>
                            )}
                            {rerunResult.removed.length > 0 && (
                              <p>
                                <b>−</b>{' '}
                                {rerunResult.removed
                                  .slice(0, RERUN_NAME_PREVIEW)
                                  .map(id => nameById.get(id) ?? id)
                                  .join(', ')}
                                {rerunResult.removed.length > RERUN_NAME_PREVIEW &&
                                  ` ${t('groups.andMore', { count: rerunResult.removed.length - RERUN_NAME_PREVIEW })}`}
                              </p>
                            )}
                          </details>
                        )}
                      </>
                    )}
                    {(rerun.recipe.manual.added.length > 0 ||
                      rerun.recipe.manual.removed.length > 0) && (
                      <label className="scope-assigned-toggle">
                        <input
                          type="checkbox"
                          checked={rerun.keepManual}
                          onChange={e =>
                            setRerun({ ...rerun, keepManual: e.target.checked })
                          }
                        />
                        <span>
                          {t('groups.keepManual', {
                            added: rerun.recipe.manual.added.length,
                            removed: rerun.recipe.manual.removed.length,
                          })}
                        </span>
                      </label>
                    )}
                  </div>
                  <div className="scope-recipe-actions">
                    <button type="button" className="scope-cancel-btn" onClick={resetFilters}>
                      {t('groups.rerunDiscard')}
                    </button>
                    <button
                      type="button"
                      className="scope-save-btn"
                      onClick={applyRerun}
                      disabled={!rerunResult}
                    >
                      {t('groups.rerunApply')}
                    </button>
                  </div>
                </div>
              )}

              <ScopeGroupFilters
                state={filterState}
                onChange={next => setFilterState(next)}
                ruleStatus={ruleStatus}
                currencies={currencies}
              />
            </div>

            <div className="scope-main-results">
              <div className="scope-list-toolbar">
                <div className="scope-search">
                  <Icon name="search" size={15} />
                  <input
                    type="search"
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      // New result list — float current picks to the top.
                      setPinnedIds(new Set(selectedIds));
                    }}
                    placeholder={t('groups.search')}
                  />
                </div>
                {selectedIds.size > 0 && (
                  <label className="scope-assigned-toggle">
                    <input
                      type="checkbox"
                      checked={selectedOnlyActive}
                      onChange={e => setAssignedOnly(e.target.checked)}
                    />
                    <span>{t('groups.selectedOnly')}</span>
                  </label>
                )}
              </div>

              <div className="scope-list-meta">
                <span>
                  {evaluationPending
                    ? t('groups.calculating', { count: profiles.length })
                    : matchCount > RESTAURANT_RENDER_CAP
                      ? t('groups.shownOf', {
                          shown: visibleRestaurants.length,
                          total: matchCount.toLocaleString(),
                        })
                      : t('groups.shown', { count: matchCount })}{' '}
                  · {t('groups.nSelected', { count: selectedIds.size })}
                </span>
                <div className="scope-list-meta-actions">
                  {allMatchesSelected ? (
                    <button
                      type="button"
                      className="scope-link-btn"
                      onClick={clearAllMatches}
                    >
                      {t('groups.clearAllMatches', { count: matchCount })}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="scope-link-btn"
                      onClick={selectAllMatches}
                      disabled={!matchCount || evaluationPending}
                    >
                      {t('groups.selectAllMatches', { count: matchCount })}
                    </button>
                  )}
                  {hasActiveFilters && (
                    <button
                      type="button"
                      className="scope-link-btn"
                      onClick={resetFilters}
                    >
                      {t('groups.resetFilters')}
                    </button>
                  )}
                </div>
              </div>

              <div className="scope-rest-list">
                {listLoading && (
                  <div className="scope-group-empty">{t('groups.loading')}</div>
                )}
                {!listLoading && evaluationPending && (
                  <div className="scope-group-empty" role="status">
                    <span className="scope-spinner" aria-hidden="true" />{' '}
                    {t('groups.calculating', { count: profiles.length })}
                  </div>
                )}
                {!listLoading && !evaluationPending && evaluationBroken && (
                  <div className="scope-group-empty is-error">
                    {t('groups.filtersIncomplete')}
                  </div>
                )}
                {!listLoading &&
                  evaluationReady &&
                  visibleRestaurants.map(r => {
                    const on = selectedIds.has(r.id);
                    const chips = ruleValueChips(r, filterState, kpiByRule);
                    return (
                      <label
                        key={r.id}
                        className={`scope-rest-row${on ? ' on' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleRestaurant(r.id)}
                        />
                        <div className="scope-rest-main">
                          <b>{r.name}</b>
                          <span>
                            {[r.city, i18n.t(`chrome:scopeFilters.regions.${r.region}`, { defaultValue: r.region })]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                          {chips.length > 0 && (
                            <span className="scope-rest-chips">
                              {chips.map(chip => (
                                <span key={chip} className="scope-rest-chip">
                                  {chip}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                {!listLoading &&
                  evaluationReady &&
                  matchCount > RESTAURANT_RENDER_CAP && (
                    <div className="scope-group-empty">
                      {t('groups.showingFirst', {
                        cap: RESTAURANT_RENDER_CAP,
                        total: matchCount.toLocaleString(),
                      })}
                    </div>
                  )}
                {!listLoading && evaluationReady && !matchCount && (
                  <div className="scope-group-empty">
                    {selectedOnlyActive
                      ? t('groups.noSelectedMatch')
                      : t('groups.noMatch')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && <div className="scope-modal-error">{error}</div>}

        <div className="scope-modal-foot">
          {activeGroupId && !creatingNew && !isBuiltinGroup && (
            <button
              type="button"
              className="scope-delete-btn"
              onClick={handleDelete}
              disabled={saving}
            >
              {t('groups.deleteGroup')}
            </button>
          )}
          <div className="scope-modal-foot-actions">
            <button
              type="button"
              className="scope-cancel-btn"
              onClick={onClose}
              disabled={saving}
            >
              {t('groups.cancel')}
            </button>
            <button
              type="button"
              className="scope-save-btn"
              onClick={handleSave}
              disabled={
                saving ||
                isBuiltinGroup ||
                !draftName.trim() ||
                !selectedIds.size ||
                !!rerun
              }
            >
              {saving
                ? t('groups.saving')
                : creatingNew
                  ? t('groups.create')
                  : t('groups.save')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
